import { Back, Sine, TimelineLite } from "gsap";
import * as HLF from "html-living-framework";
import { Application } from "../Application";
import { AppProgress } from "../Data/AppProgress";
import * as Constants from "../Data/Constants";
import { Regions, RegionType } from "../Data/Regions";
import * as VoiceOver from "../Data/VoiceOverKeys";
import { BalloonSelectable } from "../GameObjects/BalloonSelectable";
import { Button } from "../GameObjects/Button";
import { Fabuloso } from "../GameObjects/Fabuloso";
import { KeyboardFocusManager } from "../GameObjects/KeyboardFocusManager";
import { ParallaxLayer } from "../GameObjects/ParallaxLayer";
import { SelectionProgress } from "../GameObjects/SelectionProgress";
import { InteruptType, VoiceCategory } from "../GameObjects/VoiceOverManager";

export class BalloonSelection extends HLF.State {
    private static readonly BALLOON_COUNT: number = 8;

    private _isAllUnlocked: boolean;

    private _bg: PIXI.Sprite;
    private _clouds: ParallaxLayer;
    private _balloonContainer: PIXI.Container;
    private _balloons: BalloonSelectable[];
    private _radioGroup: HLF.Input.RadioGroup<BalloonSelectable>;
    private _buttonContinue: Button;
    private _progress: SelectionProgress;
    private _fabuloso: Fabuloso;
    private _overlay: PIXI.Sprite;
    private _keyboardFocusManager: KeyboardFocusManager;

    private _timeline: TimelineLite | undefined;

    constructor() {
        super();
        this._isAllUnlocked = AppProgress.levelsCompleted >= 8;

        this._bg = HLF.PixiUtils.makeSprite("sky");
        this.addChild(this._bg);

        this._clouds = new ParallaxLayer(
            Constants.PARALLAX_SPEED * 0.5,
            Regions.clouds,
            new PIXI.Point(-400, -100),
            new PIXI.Point(0, 700),
            new PIXI.Point(0.6, 1.0),
        );
        this.addChild(this._clouds);

        this._progress = new SelectionProgress(AppProgress.activeLevel);
        this._progress.position.set(0, 325);
        this._progress.visible = AppProgress.levelsCompleted < 8;
        this.addChild(this._progress);

        this._fabuloso = new Fabuloso();
        this._fabuloso.scale.x = this._fabuloso.scale.x * -1;
        this.addChild(this._fabuloso);

        this._radioGroup = new HLF.Input.RadioGroup<BalloonSelectable>();
        this._balloonContainer = new PIXI.Container();
        this.addChild(this._balloonContainer);

        this._balloons = new Array<BalloonSelectable>(BalloonSelection.BALLOON_COUNT);
        for (let i = 0; i < this._balloons.length; ++i) {
            const index: number = i;
            this._balloons[i] = new BalloonSelectable(
                () => { this.onBalloonPressed(index); },
                index,
                AppProgress.levelsCompleted - 1 >= index, // instantly unlock previously unlocked balloons
                i === this._balloons.length - 1,
            );
            this._balloons[i].position.set(
                i < 5 ? (-400 + 200 * i) : (-220 + 220 * (i - 5)),
                i < 5 ? -220 : i < 7 ? 100 : 130,
            );
            this._balloonContainer.addChild(this._balloons[i]);
            this._radioGroup.addElement(this._balloons[i]);
        }
        if (this._isAllUnlocked) {
            this._balloons[AppProgress.activeBalloon].select();
        }

        this._buttonContinue = new Button(
            this.onContinuePressed.bind(this),
            "button-play", Constants.SPRITESHEET_UI,
            VoiceOver.NEXT,
        );
        this._buttonContinue.scale.set(0.75);
        this.addChild(this._buttonContinue);

        this._keyboardFocusManager = new KeyboardFocusManager();
        this.addChild(this._keyboardFocusManager);

        this._overlay = HLF.PixiUtils.makeSprite("white-pixel", Constants.SPRITESHEET_UI);
        this._overlay.tint = 0x000000;
        this.addChild(this._overlay);

        PubSub.publishSync(HLF.PUSH_KEYBOARD_LAYER, undefined);

        if (AppProgress.levelsCompleted >= RegionType.NUM_ELEMENTS) {
            Application.instance.voiceOver.play(
                HLF.ArrayUtils.random(VoiceOver.SELECT_IDLE),
                InteruptType.LESS,
                VoiceCategory.INSTRUCTIONAL);
        }
    }

    public static getRequiredAssets(): string[] {
        const requiredAssets: string[] = [
            "sky" + HLF.ResourceUtils.FILE_EXTENSION_JPG,
            Constants.SPRITESHEET_GAMEPLAY + HLF.ResourceUtils.FILE_EXTENSION_JSON,
            Constants.SPRITESHEET_TERRAIN_PREFIX + "common" + HLF.ResourceUtils.FILE_EXTENSION_JSON,
        ];
        return requiredAssets;
    }

    private get newestBalloon(): BalloonSelectable {
        return this._balloons[Math.min(AppProgress.levelsCompleted, this._balloons.length - 1)];
    }

    public init(pSize: PIXI.Point): void {
        super.init(pSize);
        this._clouds.init(pSize);
    }

    public animateIn(pOnComplete: () => void): void {
        pOnComplete();

        this.scale.set(1.1);

        if (this._timeline !== undefined) { this._timeline.kill(); }
        this._timeline = new TimelineLite();

        this._timeline.to(this.scale, 1.0, {
            x: 1,
            y: 1,
            ease: Sine.easeInOut,
        }, 0);
        this._timeline.to(this._overlay, 0.75, {
            alpha: 0,
            ease: Sine.easeInOut,
        }, 0);

        if (this._isAllUnlocked === false) {
            this.interactiveChildren = false;
            this._buttonContinue.y = this._size.y * 0.5 + this._buttonContinue.height * 0.5;

            this._timeline.call(() => {
                HLF.PixiUtils.sendToFront(this.newestBalloon);
                this.newestBalloon.unlock();
            }, undefined, this, 1.0);
            this._timeline.call(() => {
                this._progress.animateBalloon();
            }, undefined, this, 1.0);
            this._timeline.call(() => {
                this._fabuloso.playSelectionAnim();
            }, undefined, this, 1.5);
            this._timeline.to(this._buttonContinue, 0.75, {
                y: this._size.y * 0.5 - this._buttonContinue.height * 0.5 - 15,
                ease: Back.easeOut,
            }, 2.0);
            this._timeline.call(() => {
                this.interactiveChildren = true;
            }, undefined, this, 2.5);
            this._timeline.call(this.initKeyboard.bind(this), undefined, this, 2.5);
        }
        else {
            this._timeline.call(this.initKeyboard.bind(this), undefined, this, 0.5);
        }
    }

    public animateOut(pOnComplete: () => void): void {
        if (this._timeline !== undefined) { this._timeline.kill(); }
        this._timeline = new TimelineLite();

        this._timeline.to(this.scale, 1.0, {
            x: 1.05,
            y: 1.05,
            ease: Sine.easeInOut,
        }, 0);
        this._timeline.to(this._overlay, 0.75, {
            alpha: 1,
            ease: Sine.easeInOut,
        }, 0.25);
        this._timeline.call(pOnComplete);
    }

    public update(pDeltaTime: number): void {
        super.update(pDeltaTime);
        this._fabuloso.update(pDeltaTime);
        for (let i = 0; i < this._balloons.length; ++i) {
            this._balloons[i].update(pDeltaTime);
        }
        this._clouds.update(pDeltaTime);
    }

    public onResize(pSize: PIXI.Point): void {
        super.onResize(pSize);

        this._fabuloso.position.set(
            -this._size.x * 0.5 + 125,
            this._size.y * 0.5 + 30,
        );
        this._buttonContinue.position.set(
            this._size.x * 0.5 - this._buttonContinue.width * 0.5 - 15,
            this._size.y * 0.5 - this._buttonContinue.height * 0.5 - 15,
        );
        this._clouds.onResize(pSize);
        this._overlay.scale.set(this._size.x, this._size.y);
    }

    public destroy(pOptions: boolean | PIXI.DestroyOptions = { children: true }): void {
        super.destroy(pOptions);
        if (this._timeline !== undefined) { this._timeline.kill(); }
    }

    private initKeyboard(): void {
        PubSub.publishSync(HLF.PUSH_KEYBOARD_LAYER, undefined);
        const focusables: HLF.Input.IFocusable[] = new Array<HLF.Input.IFocusable>();
        for (let i = 0; i < Math.min(AppProgress.levelsCompleted + 1, this._balloons.length); ++i) {
            focusables.push(this._balloons[i]);
        }
        focusables.push(this._buttonContinue);
        PubSub.publishSync(HLF.REGISTER_FOCUSABLES, focusables);
    }

    private onBalloonPressed(pIndex: number): void {
        AppProgress.activeBalloon = pIndex;
    }

    private onContinuePressed(): void {
        this._buttonContinue.interactive = false;
        PubSub.publishSync(HLF.POP_KEYBOARD_LAYER, undefined);
        AppProgress.activeLevel = AppProgress.levelsCompleted % RegionType.NUM_ELEMENTS;
        PubSub.publishSync(HLF.STATE_TRANSITION, new HLF.StateToken(
            Constants.STATE_GAME,
            Constants.LOAD_INTERSTITIAL,
        ));
    }
}
